import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Building, Clock, AlertCircle, CheckCircle, XCircle,
  Bell, BellOff, TestTube, Settings2, Navigation, Crosshair, Timer,
  Sun, Moon, MapPin, RefreshCw, Info, Lock,
  Camera, LocateFixed, ShieldCheck, ShieldAlert, ShieldOff, CalendarClock, Languages,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistance } from '../utils/distance';
import { notificationManager } from '../utils/notificationManager';
import { settingsService } from '../services/settings';
import { useLanguage } from '../contexts/LanguageContext';
import type { ApiSite, ApiShift } from '../types';

// Calculated shift duration helper
function getShiftDuration(start: string, end: string): string {
  if (!start || !end) return '0h 0m';
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let totalMin = (eh * 60 + em) - (sh * 60 + sm);
  if (totalMin < 0) totalMin += 24 * 60; // overnight shift
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// Settings section wrapper
function SettingsSection({
  icon: Icon,
  iconBg,
  iconColor,
  topStripe,
  title,
  description,
  children,
  badge,
}: {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  topStripe: string;
  title: string;
  description: string;
  children: React.ReactNode;
  badge?: React.ReactNode;
}): React.JSX.Element {
  return (
    <Card className="shadow-md overflow-hidden">
      <div className="h-1 w-full" style={{ background: topStripe }} />
      <CardHeader className="pb-4 pt-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg flex-shrink-0"
              style={{ background: iconBg }}
            >
              <Icon className="h-5 w-5" style={{ color: iconColor }} />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-[16px] font-black">{title}</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{description}</p>
            </div>
          </div>
          {badge && <div className="flex-shrink-0 mt-0.5">{badge}</div>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 px-4 pb-5">{children}</CardContent>
    </Card>
  );
}

export default function Settings(): React.JSX.Element {
  const { t, language, setLanguage } = useLanguage();
  const [sites, setSites] = useState<ApiSite[]>([]);
  const [shifts, setShifts] = useState<ApiShift[]>([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingShifts, setLoadingShifts] = useState(true);
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifPermission, setNotifPermission] = useState<NotificationPermission>('default');
  const [locationPermission, setLocationPermission] = useState<PermissionState | 'unsupported'>('prompt');
  const [cameraPermission, setCameraPermission] = useState<PermissionState | 'unsupported'>('prompt');
  const [checkingPermissions, setCheckingPermissions] = useState(false);

  const fetchSites = useCallback(async () => {
    setLoadingSites(true);
    try {
      const data = await settingsService.getSites();
      setSites(data);
    } catch {
      toast.error(t.managedByOrg, { description: 'Failed to load office locations' });
    } finally {
      setLoadingSites(false);
    }
  }, [t]);

  const fetchShifts = useCallback(async () => {
    setLoadingShifts(true);
    try {
      const data = await settingsService.getMyShifts();
      setShifts(data);
    } catch {
      toast.error(t.shiftSchedule, { description: 'Failed to load shift schedule' });
    } finally {
      setLoadingShifts(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchSites();
    void fetchShifts();
    const settings = notificationManager.getSettings();
    setNotifEnabled(settings.enabled);
    setNotifPermission(notificationManager.getCurrentPermission());
    void checkBrowserPermissions();
  }, [fetchSites, fetchShifts]);

  // ── Browser Permissions ─────────────────────────────────────────────────
  const checkBrowserPermissions = async (): Promise<void> => {
    if (!navigator.permissions) {
      setLocationPermission('unsupported');
      setCameraPermission('unsupported');
      return;
    }
    try {
      const [loc, cam] = await Promise.all([
        navigator.permissions.query({ name: 'geolocation' }),
        navigator.permissions.query({ name: 'camera' as PermissionName }),
      ]);
      setLocationPermission(loc.state);
      setCameraPermission(cam.state);
      // Live updates when user changes permission in browser
      loc.onchange = () => setLocationPermission(loc.state);
      cam.onchange = () => setCameraPermission(cam.state);
    } catch {
      // camera query not supported in some browsers
      try {
        const loc = await navigator.permissions.query({ name: 'geolocation' });
        setLocationPermission(loc.state);
        loc.onchange = () => setLocationPermission(loc.state);
      } catch {
        setLocationPermission('unsupported');
      }
      setCameraPermission('unsupported');
    }
  };

  const requestLocationPermission = async (): Promise<void> => {
    setCheckingPermissions(true);
    try {
      await new Promise<GeolocationPosition>((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000 })
      );
      setLocationPermission('granted');
      toast.success(t.allowLocation, {
        description: t.locationGrantedInfo,
      });
    } catch (err: unknown) {
      const code = (err as GeolocationPositionError)?.code;
      if (code === 1) {
        setLocationPermission('denied');
        toast.error(t.location, {
          description: t.locationDenied,
        });
      } else {
        toast.error(t.location, {
          description: 'Make sure GPS is enabled on your device.',
        });
      }
    } finally {
      setCheckingPermissions(false);
    }
  };

  const requestCameraPermission = async (): Promise<void> => {
    setCheckingPermissions(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((t) => t.stop());
      setCameraPermission('granted');
      toast.success(t.allowCamera, {
        description: t.cameraGrantedInfo,
      });
    } catch (err: unknown) {
      const name = (err as DOMException)?.name;
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setCameraPermission('denied');
        toast.error(t.camera, {
          description: t.cameraDenied,
        });
      } else {
        toast.error(t.camera, {
          description: 'Your device may not have a camera.',
        });
      }
    } finally {
      setCheckingPermissions(false);
    }
  };

  const handleRefreshPermissions = async (): Promise<void> => {
    setCheckingPermissions(true);
    await checkBrowserPermissions();
    setCheckingPermissions(false);
    toast.info(t.refreshPermissions);
  };

  const handleLocationToggle = (enable: boolean): void => {
    if (enable) {
      void requestLocationPermission();
    } else {
      // Browser permissions cannot be revoked from JS — guide user
      toast.info(t.location, {
        description: t.locationGrantedInfo,
        duration: 7000,
      });
    }
  };

  const handleCameraToggle = (enable: boolean): void => {
    if (enable) {
      void requestCameraPermission();
    } else {
      toast.info(t.camera, {
        description: t.cameraGrantedInfo,
        duration: 7000,
      });
    }
  };

  const handleNotificationToggle = async (enabled: boolean): Promise<void> => {
    if (enabled && notifPermission !== 'granted') {
      const permission = await notificationManager.requestPermission();
      setNotifPermission(permission);
      if (permission !== 'granted') {
        toast.error(t.notifications, { description: 'Please allow notifications in your browser settings.' });
        return;
      }
    }
    notificationManager.setEnabled(enabled);
    setNotifEnabled(enabled);
    if (enabled) {
      toast.success(t.notifications, { description: t.notifActive });
    } else {
      toast.info(t.notifDisabled);
    }
  };

  const handleTestNotification = async (): Promise<void> => {
    if (notifPermission !== 'granted') {
      toast.error(t.notifDisabled);
      return;
    }
    const sent = await notificationManager.sendTestNotification();
    if (sent) {
      toast.success(t.testNotif, { description: 'You should see a notification appear.' });
    } else {
      toast.error(t.notifications, {
        description: 'Could not send notification. Make sure the service worker is registered and notifications are allowed.',
      });
    }
  };

  // Primary site for summary display
  const primarySite = sites[0] ?? null;

  // Primary shift for summary display
  const primaryShift = shifts[0] ?? null;

  const shiftDuration = useMemo(() => {
    if (!primaryShift) return '';
    return getShiftDuration(primaryShift.start_time, primaryShift.end_time);
  }, [primaryShift]);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="settings-page">

      {/* ═══════════════════════════════════════
          HERO HEADER
      ═══════════════════════════════════════ */}
      <div
        className="relative rounded-2xl overflow-hidden bg-premium-hero"
      >
        <div className="relative px-5 pt-5 pb-6 z-10">
          {/* Label row + language switcher */}
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20 shadow-inner">
                <Settings2 className="h-4 w-4 text-white" />
              </div>
              <span className="text-[11px] font-bold text-white/80 uppercase tracking-[0.15em]">{t.navSettings}</span>
            </div>
            {/* Language toggle */}
            <div className="flex items-center gap-1.5">
              <Languages className="h-3.5 w-3.5 text-white/70" />
              <div
                className="flex rounded-lg overflow-hidden text-xs font-bold"
                style={{ background: 'rgba(0,0,0,0.20)' }}
              >
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1.5 transition-colors ${language === 'en' ? 'bg-white text-primary' : 'text-white/80 hover:text-white'}`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage('id')}
                  className={`px-3 py-1.5 transition-colors ${language === 'id' ? 'bg-white text-primary' : 'text-white/80 hover:text-white'}`}
                >
                  ID
                </button>
              </div>
            </div>
          </div>
          <h1 className="text-[26px] font-black text-white leading-tight tracking-tight">{t.settings}</h1>
          <p className="text-[12px] text-white/70 mt-0.5 font-medium">{t.settingsDesc}</p>
        </div>
      </div>

      {/* Read-only data notice */}
      <div
        className="flex items-center gap-3 p-3.5 rounded-2xl border"
        style={{ background: 'rgba(240,130,0,0.06)', borderColor: 'rgba(240,130,0,0.25)' }}
      >
        <div
          className="flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0"
          style={{ background: 'var(--gradient-primary)' }}
        >
          <Lock className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-foreground">{t.managedByOrg}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t.managedByOrgDesc}</p>
        </div>
      </div>

      {/* Main 2-column layout */}
      <div className="grid gap-6">
        {/* Left column (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* ===== Office Location (Read-Only from ERP) ===== */}
          <SettingsSection
            icon={Building}
            iconBg="hsl(var(--primary) / 0.15)"
            iconColor="hsl(var(--primary))"
            topStripe="var(--gradient-primary)"
            title={t.officeLocation}
            description={t.officeLocationDesc}
            badge={
              <Badge variant="secondary" className="gap-1 text-xs">
                <Lock className="h-3 w-3" /> {t.readOnly}
              </Badge>
            }
          >
            {loadingSites ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <span className="ml-3 text-sm text-muted-foreground">{t.loadingOfficeLocations}</span>
              </div>
            ) : sites.length === 0 ? (
              <div className="flex flex-col items-center gap-4 py-8 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted/50">
                  <MapPin className="h-8 w-8 text-muted-foreground/40" />
                </div>
                <div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs font-medium border border-border mb-2">
                    <XCircle className="h-3.5 w-3.5" /> {t.notAssigned}
                  </div>
                  <p className="text-sm font-medium text-foreground">{t.noOfficeLocations}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t.noOfficeLocationsDesc}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {sites.map((site) => (
                  <div key={site.id} className="p-4 bg-muted/30 rounded-xl border border-border space-y-4">
                    {/* Site name */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-primary" />
                        <span className="text-sm font-semibold text-foreground">{site.name || `Site #${site.id}`}</span>
                      </div>
                      <Badge variant="default" className="gap-1 bg-success/10 text-success border-success/20 hover:bg-success/20">
                        <CheckCircle className="h-3 w-3" /> {t.active}
                      </Badge>
                    </div>

                    {/* GPS Coordinates (read-only) */}
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">{t.gpsCoordinates}</Label>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 p-2.5 bg-card rounded-lg border border-border">
                          <span className="text-xs text-muted-foreground font-medium min-w-[24px]">Lat</span>
                          <span className="text-sm font-mono text-foreground">{site.latitude.toFixed(6)}</span>
                        </div>
                        <div className="flex items-center gap-2 p-2.5 bg-card rounded-lg border border-border">
                          <span className="text-xs text-muted-foreground font-medium min-w-[24px]">Lng</span>
                          <span className="text-sm font-mono text-foreground">{site.longitude.toFixed(6)}</span>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Geofence Radius (read-only) */}
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">{t.geofenceRadius}</Label>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 p-2.5 bg-card rounded-lg border border-border flex-1">
                          <Navigation className="h-4 w-4 text-primary" />
                          <span className="text-sm font-semibold text-foreground">{site.radius_meter} {t.meters}</span>
                          <Badge variant="secondary" className="ml-auto font-semibold text-xs">{formatDistance(site.radius_meter)}</Badge>
                        </div>
                      </div>

                      {/* Visual geofence indicator */}
                      <div className="flex items-center justify-center py-4">
                        <div
                          className="rounded-full border-2 border-dashed border-primary/30 flex items-center justify-center transition-all duration-300"
                          style={{ width: '120px', height: '120px' }}
                        >
                          <div className="rounded-full bg-primary/10 flex items-center justify-center" style={{ width: '55%', height: '55%' }}>
                            <div className="flex flex-col items-center">
                              <Building className="h-4 w-4 text-primary" />
                              <span className="text-[9px] text-primary font-medium mt-0.5">Office</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="text-[11px] text-center text-muted-foreground">
                        {t.mustBeWithin(formatDistance(site.radius_meter))}
                      </p>
                    </div>
                  </div>
                ))}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void fetchSites()}
                  className="gap-2 text-xs text-muted-foreground"
                >
                  <RefreshCw className="h-3 w-3" />
                  {t.refreshLocations}
                </Button>
              </div>
            )}
          </SettingsSection>

          {/* ===== Shift Schedule (Read-Only from ERP) ===== */}
          <SettingsSection
            icon={Clock}
            iconBg="hsl(var(--accent) / 0.15)"
            iconColor="hsl(var(--accent))"
            topStripe="linear-gradient(135deg, hsl(var(--accent)) 0%, hsl(var(--warning)) 100%)"
            title={t.shiftSchedule}
            description={t.shiftScheduleDesc}
            badge={
              <Badge variant="secondary" className="gap-1 text-xs">
                <Lock className="h-3 w-3" /> {t.readOnly}
              </Badge>
            }
          >
            {loadingShifts ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <span className="ml-3 text-sm text-muted-foreground">{t.loadingShiftSchedule}</span>
              </div>
            ) : shifts.length === 0 ? (
              <div className="flex flex-col items-center gap-4 py-8 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted/50">
                  <Clock className="h-8 w-8 text-muted-foreground/40" />
                </div>
                <div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs font-medium border border-border mb-2">
                    <XCircle className="h-3.5 w-3.5" /> {t.notAssigned}
                  </div>
                  <p className="text-sm font-medium text-foreground">{t.noShiftAssigned}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t.noShiftAssignedDesc}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {shifts.map((shift) => {
                  const duration = getShiftDuration(shift.start_time, shift.end_time);
                  const formattedDate = shift.effective_date
                    ? new Date(shift.effective_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : null;
                  return (
                    <div key={`${shift.id}-${shift.effective_date}`} className="p-4 bg-muted/30 rounded-xl border border-border space-y-4">
                      {/* Shift name + status badge */}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-foreground">{shift.name}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Badge variant="secondary" className="gap-1 font-mono">
                            <Timer className="h-3 w-3" />
                            {duration}
                          </Badge>
                          {shift.is_current ? (
                            <Badge className="gap-1 bg-success/10 text-success border-success/20 hover:bg-success/20 text-xs">
                              <CheckCircle className="h-3 w-3" /> {t.activeShift}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="gap-1 text-blue-600 border-blue-300 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800 text-xs">
                              <CalendarClock className="h-3 w-3" /> {t.upcomingShift}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Effective date */}
                      {formattedDate && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <CalendarClock className="h-3 w-3 flex-shrink-0" />
                          {shift.is_current ? t.activeFrom(formattedDate) : t.effectiveFrom(formattedDate)}
                        </p>
                      )}

                      {/* Time display (read-only) */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-card rounded-lg border border-border text-center">
                          <div className="flex items-center justify-center gap-1.5 mb-1.5">
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-success-light flex-shrink-0">
                              <Sun className="h-3 w-3 text-success" />
                            </div>
                            <span className="text-xs font-medium text-muted-foreground">{t.start}</span>
                          </div>
                          <span className="text-lg font-semibold text-foreground">{shift.start_time}</span>
                        </div>
                        <div className="p-3 bg-card rounded-lg border border-border text-center">
                          <div className="flex items-center justify-center gap-1.5 mb-1.5">
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-warning-light flex-shrink-0">
                              <Moon className="h-3 w-3 text-warning" />
                            </div>
                            <span className="text-xs font-medium text-muted-foreground">{t.end}</span>
                          </div>
                          <span className="text-lg font-semibold text-foreground">{shift.end_time}</span>
                        </div>
                      </div>

                      {/* Timeline bar (read-only visual) */}
                      <div className="pt-2">
                        <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                          <div className="absolute inset-y-0 bg-gradient-to-r from-success via-primary to-warning rounded-full" style={{ left: '0%', right: '0%' }} />
                        </div>
                        <div className="flex justify-between mt-1.5">
                          <span className="text-[10px] font-medium text-success">{shift.start_time}</span>
                          <span className="text-[10px] font-semibold text-primary">{duration} {t.total}</span>
                          <span className="text-[10px] font-medium text-warning">{shift.end_time}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void fetchShifts()}
                  className="gap-2 text-xs text-muted-foreground"
                >
                  <RefreshCw className="h-3 w-3" />
                  {t.refreshShiftSchedule}
                </Button>
              </div>
            )}
          </SettingsSection>
        </div>

        {/* Right column (1/3) — sidebar */}
        <div className="lg:col-span-2 space-y-6">
          {/* ===== Notifications ===== */}
          <SettingsSection
            icon={Bell}
            iconBg={notifEnabled ? 'hsl(var(--success) / 0.15)' : 'hsl(var(--muted))'}
            iconColor={notifEnabled ? 'hsl(var(--success))' : 'hsl(var(--muted-foreground))'}
            topStripe={notifEnabled ? 'linear-gradient(135deg, hsl(var(--success)) 0%, hsl(140 60% 35%) 100%)' : 'hsl(var(--muted))'}
            title={t.notifications}
            description={t.notificationsDesc}
          >
            {!notificationManager.isSupported() ? (
              <div className="p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground flex items-center gap-3">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                {t.browserNotificationsUnsupported}
              </div>
            ) : (
              <>
                {/* Main toggle */}
                <div className="flex items-center justify-between gap-3 p-3.5 rounded-xl bg-muted/30 border border-border">
                  <div className="flex items-center gap-3">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${notifEnabled ? 'bg-success-light' : 'bg-muted'}`}>
                      {notifEnabled ? (
                        <Bell className="h-4 w-4 text-success" />
                      ) : (
                        <BellOff className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t.dailyReminder}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {notifEnabled ? t.notifActive : t.notifDisabled}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={notifEnabled}
                    onCheckedChange={(checked) => void handleNotificationToggle(checked)}
                    data-testid="notifications-toggle"
                  />
                </div>

                {/* Notification features when enabled */}
                {notifEnabled && (
                  <div className="space-y-2.5 animate-fade-in">
                    {[
                      { label: t.preShiftReminder, desc: t.preShiftReminderDesc, active: true },
                      { label: t.lateCheckinAlert, desc: t.lateCheckinAlertDesc, active: true },
                      { label: t.postCheckinConfirmation, desc: t.postCheckinConfirmationDesc, active: true },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-card border border-border">
                        <CheckCircle className="h-3.5 w-3.5 text-success flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground">{item.label}</p>
                          <p className="text-[10px] text-muted-foreground">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <Separator />

                {/* Permission status */}
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{t.permission}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      {notifPermission === 'granted' && <CheckCircle className="h-3.5 w-3.5 text-success" />}
                      {notifPermission === 'denied' && <AlertCircle className="h-3.5 w-3.5 text-destructive" />}
                      {notifPermission === 'default' && <AlertCircle className="h-3.5 w-3.5 text-warning" />}
                      <Badge
                        variant={notifPermission === 'granted' ? 'success' : notifPermission === 'denied' ? 'destructive' : 'warning'}
                        className="text-xs capitalize"
                        data-testid="notification-permission-badge"
                      >
                        {notifPermission === 'granted' ? t.notifGranted : notifPermission === 'denied' ? t.notifDenied : notifPermission === 'default' ? t.notifDefault : t.notifUnsupported}
                      </Badge>
                    </div>
                  </div>
                  {notifPermission === 'granted' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleTestNotification()}
                      className="gap-2"
                      data-testid="test-notification-btn"
                    >
                      <TestTube className="h-3.5 w-3.5" />
                      {t.testNotif}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => void handleNotificationToggle(true)}
                      className="gap-2"
                      data-testid="request-permission-btn"
                    >
                      <Bell className="h-3.5 w-3.5" />
                      {t.allowNotif}
                    </Button>
                  )}
                </div>

                {notifPermission === 'denied' && (
                  <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-lg text-xs text-destructive animate-fade-in">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{t.notifBlockedSettings}</span>
                  </div>
                )}
              </>
            )}
          </SettingsSection>

          {/* ===== App Permissions ===== */}
          {(() => {
            type PermStatus = PermissionState | 'unsupported';

            const permIcon = (s: PermStatus) => {
              if (s === 'granted')     return <ShieldCheck className="h-4 w-4 text-success" />;
              if (s === 'denied')      return <ShieldOff className="h-4 w-4 text-destructive" />;
              if (s === 'unsupported') return <ShieldAlert className="h-4 w-4 text-muted-foreground" />;
              return <ShieldAlert className="h-4 w-4 text-warning" />;  // prompt
            };

            const permBadge = (s: PermStatus) => {
              const variants: Record<PermStatus, 'success' | 'destructive' | 'warning' | 'secondary'> = {
                granted: 'success', denied: 'destructive', prompt: 'warning', unsupported: 'secondary',
              };
              const labels: Record<PermStatus, string> = {
                granted: t.permGranted, denied: t.permDenied, prompt: t.permPrompt, unsupported: t.permUnsupported,
              };
              return (
                <Badge variant={variants[s]} className="text-xs capitalize">
                  {labels[s]}
                </Badge>
              );
            };

            const overallGranted =
              locationPermission === 'granted' && cameraPermission === 'granted';

            return (
              <SettingsSection
                icon={ShieldCheck}
                iconBg={overallGranted ? 'hsl(var(--success) / 0.15)' : 'hsl(var(--warning) / 0.15)'}
                iconColor={overallGranted ? 'hsl(var(--success))' : 'hsl(var(--warning))'}
                topStripe={overallGranted ? 'linear-gradient(135deg, hsl(var(--success)) 0%, hsl(140 60% 35%) 100%)' : 'linear-gradient(135deg, hsl(var(--warning)) 0%, hsl(35 90% 45%) 100%)'}
                title={t.appPermissions}
                description={t.appPermissionsDesc}
              >
                <div className="space-y-4">

                  {/* Location */}
                  <div className="p-3.5 rounded-xl bg-muted/30 border border-border space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0 ${
                          locationPermission === 'granted' ? 'bg-success-light' :
                          locationPermission === 'denied'  ? 'bg-destructive/10' : 'bg-muted'
                        }`}>
                          <LocateFixed className={`h-4 w-4 ${
                            locationPermission === 'granted' ? 'text-success' :
                            locationPermission === 'denied'  ? 'text-destructive' : 'text-muted-foreground'
                          }`} />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{t.location}</p>
                          <p className="text-[11px] text-muted-foreground">{t.locationDesc}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {permIcon(locationPermission)}
                        {permBadge(locationPermission)}
                        {locationPermission !== 'unsupported' && (
                          <Switch
                            checked={locationPermission === 'granted'}
                            onCheckedChange={handleLocationToggle}
                            disabled={checkingPermissions}
                            aria-label={t.toggleLocation}
                          />
                        )}
                      </div>
                    </div>

                    {locationPermission === 'granted' && (
                      <div className="flex items-start gap-2 p-2.5 bg-muted/50 rounded-lg text-xs text-muted-foreground">
                        <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                        <span>{t.locationGrantedInfo}</span>
                      </div>
                    )}

                    {locationPermission === 'denied' && (
                      <div className="flex items-start gap-2 p-2.5 bg-destructive/10 rounded-lg text-xs text-destructive">
                        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                        <span>{t.locationDenied}</span>
                      </div>
                    )}

                    {locationPermission === 'prompt' && (
                      <Button
                        size="sm"
                        onClick={() => void requestLocationPermission()}
                        disabled={checkingPermissions}
                        className="gap-2 w-full"
                      >
                        <LocateFixed className="h-3.5 w-3.5" />
                        {checkingPermissions ? t.requesting : t.allowLocation}
                      </Button>
                    )}
                  </div>

                  {/* Camera */}
                  <div className="p-3.5 rounded-xl bg-muted/30 border border-border space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={`flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0 ${
                          cameraPermission === 'granted' ? 'bg-success-light' :
                          cameraPermission === 'denied'  ? 'bg-destructive/10' : 'bg-muted'
                        }`}>
                          <Camera className={`h-4 w-4 ${
                            cameraPermission === 'granted' ? 'text-success' :
                            cameraPermission === 'denied'  ? 'text-destructive' : 'text-muted-foreground'
                          }`} />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{t.camera}</p>
                          <p className="text-[11px] text-muted-foreground">{t.cameraDesc}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {permIcon(cameraPermission)}
                        {permBadge(cameraPermission)}
                        {cameraPermission !== 'unsupported' && (
                          <Switch
                            checked={cameraPermission === 'granted'}
                            onCheckedChange={handleCameraToggle}
                            disabled={checkingPermissions}
                            aria-label={t.toggleCamera}
                          />
                        )}
                      </div>
                    </div>

                    {cameraPermission === 'granted' && (
                      <div className="flex items-start gap-2 p-2.5 bg-muted/50 rounded-lg text-xs text-muted-foreground">
                        <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                        <span>{t.cameraGrantedInfo}</span>
                      </div>
                    )}

                    {cameraPermission === 'denied' && (
                      <div className="flex items-start gap-2 p-2.5 bg-destructive/10 rounded-lg text-xs text-destructive">
                        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                        <span>{t.cameraDenied}</span>
                      </div>
                    )}

                    {cameraPermission === 'prompt' && (
                      <Button
                        size="sm"
                        onClick={() => void requestCameraPermission()}
                        disabled={checkingPermissions}
                        className="gap-2 w-full"
                      >
                        <Camera className="h-3.5 w-3.5" />
                        {checkingPermissions ? t.requesting : t.allowCamera}
                      </Button>
                    )}
                  </div>

                  {/* All granted banner */}
                  {overallGranted && (
                    <div className="flex items-center gap-2.5 p-3 bg-success/10 border border-success/20 rounded-xl animate-fade-in">
                      <ShieldCheck className="h-4 w-4 text-success flex-shrink-0" />
                      <p className="text-xs text-success font-medium">{t.allPermissionsGranted}</p>
                    </div>
                  )}

                  {/* Refresh */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void handleRefreshPermissions()}
                    disabled={checkingPermissions}
                    className="gap-2 text-xs text-muted-foreground"
                  >
                    <RefreshCw className="h-3 w-3" />
                    {t.refreshPermissions}
                  </Button>
                </div>
              </SettingsSection>
            );
          })()}

          {/* ===== Quick Summary ===== */}
          <Card className="shadow-md overflow-hidden">
            <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, hsl(var(--muted-foreground)/0.3), hsl(var(--muted-foreground)/0.1))' }} />
            <CardHeader className="pb-3 pt-5">
              <div className="flex items-center gap-3">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-lg flex-shrink-0"
                  style={{ background: 'hsl(var(--muted))' }}
                >
                  <Settings2 className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <CardTitle className="text-[16px] font-black">{t.currentConfig}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pb-5 space-y-2.5">
              <div className="divide-y divide-border rounded-xl border overflow-hidden">
                <div className="flex items-center justify-between px-3.5 py-2.5 bg-card">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1.5">
                    <Building className="h-3 w-3" /> {t.office}
                  </span>
                  <span className="text-xs font-semibold text-foreground truncate max-w-[140px]">{primarySite?.name || '—'}</span>
                </div>
                <div className="flex items-center justify-between px-3.5 py-2.5 bg-muted/20">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1.5">
                    <Crosshair className="h-3 w-3" /> {t.coordinates}
                  </span>
                  <span className="font-mono text-foreground text-[11px]">
                    {primarySite ? `${primarySite.latitude.toFixed(4)}, ${primarySite.longitude.toFixed(4)}` : '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between px-3.5 py-2.5 bg-card">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1.5">
                    <Navigation className="h-3 w-3" /> {t.radius}
                  </span>
                  <span className="text-xs font-semibold text-foreground">{primarySite ? formatDistance(primarySite.radius_meter) : '—'}</span>
                </div>
                <div className="flex items-center justify-between px-3.5 py-2.5 bg-muted/20">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1.5">
                    <Clock className="h-3 w-3" /> {t.shift}
                  </span>
                  <span className="text-xs font-semibold text-foreground">{primaryShift?.name || '—'}</span>
                </div>
                <div className="flex items-center justify-between px-3.5 py-2.5 bg-card">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1.5">
                    <Timer className="h-3 w-3" /> {t.hours}
                  </span>
                  <span className="text-xs font-semibold text-foreground">
                    {primaryShift ? `${primaryShift.start_time} – ${primaryShift.end_time}` : '--:-- – --:--'}
                  </span>
                </div>
                <div className="flex items-center justify-between px-3.5 py-2.5 bg-muted/20">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1.5">
                    <Bell className="h-3 w-3" /> {t.notifications}
                  </span>
                  <Badge variant={notifEnabled ? 'success' : 'secondary'} className="text-[10px] h-5 px-1.5">
                    {notifEnabled ? t.on : t.off}
                  </Badge>
                </div>
                <div className="flex items-center justify-between px-3.5 py-2.5 bg-card">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1.5">
                    <LocateFixed className="h-3 w-3" /> {t.location}
                  </span>
                  <Badge
                    variant={locationPermission === 'granted' ? 'success' : locationPermission === 'denied' ? 'destructive' : 'warning'}
                    className="text-[10px] h-5 px-1.5 capitalize"
                  >
                    {locationPermission === 'prompt' ? t.pending : locationPermission === 'granted' ? t.permGranted : locationPermission === 'denied' ? t.permDenied : t.permUnsupported}
                  </Badge>
                </div>
                <div className="flex items-center justify-between px-3.5 py-2.5 bg-muted/20">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1.5">
                    <Camera className="h-3 w-3" /> {t.camera}
                  </span>
                  <Badge
                    variant={cameraPermission === 'granted' ? 'success' : cameraPermission === 'denied' ? 'destructive' : 'warning'}
                    className="text-[10px] h-5 px-1.5 capitalize"
                  >
                    {cameraPermission === 'prompt' ? t.pending : cameraPermission === 'granted' ? t.permGranted : cameraPermission === 'denied' ? t.permDenied : t.permUnsupported}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
